import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
} from '@mui/material';
import { Users as UsersIcon, Search, Plus, X, Pencil, Trash2, UserPlus, UserMinus } from 'lucide-react';
import { authProvider } from '../utils/authProvider';
import { useToast } from '../layout/Layout';
import { listUsers, User } from '../services/settingsServices/usersService';
import { useUserPermissions } from '../context/UserPermissionsContext';
import { broadcastDCChange } from '../utils/dcEvents';
import {
    createGroup,
    deleteGroup,
    Group,
    listGroupsByDC,
    updateGroup,
} from '../services/settingsServices/groupsService';
import { ALL_DCS } from '../utils/DCoption';
import './Groups.css';

const getInitials = (first: string, last: string) =>
    `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

// Stable avatar color class per user, so the same person looks consistent.
const AVATAR_CLASSES = ['av-j', 'av-s', 'av-a', 'av-d'];

const avatarClassFor = (userId: string): string => {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
    }
    return AVATAR_CLASSES[hash % AVATAR_CLASSES.length];
};


interface GroupForm {
    groupId: string;
    description: string;
}

const EMPTY_FORM: GroupForm = { groupId: '', description: '' };

const VALIDATION = {
    groupName: {
        regex: /^[A-Za-z0-9\s._-]+$/,
        message: 'Group Name must contain only letters, numbers, spaces, dots, hyphens, or underscores',
    },
};

const Groups = () => {
    const { userId: actorId } = authProvider();
    const toast = useToast();
    const { isAdmin, userPermissions } = useUserPermissions();

    const [groups, setGroups] = useState<Group[]>([]);
    const [dcList, setDcList] = useState<string[]>([]);
    const [selectedDC, setSelectedDC] = useState(() => sessionStorage.getItem('selectedDC') || '');
    const [users, setUsers] = useState<User[]>([]);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState<GroupForm>(EMPTY_FORM);
    const [errors, setErrors] = useState<Partial<Record<keyof GroupForm, string>>>({});
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [loadingGroups, setLoadingGroups] = useState(false);

    // Phase 1: build DC dropdown — admin sees all DCs, non-admin sees only their assigned DCs
    useEffect(() => {
        if (isAdmin) {
            setDcList(ALL_DCS);
        } else if (userPermissions) {
            const dcs = Array.from(new Set(userPermissions.roles.map((r) => r.dcCode))).sort((a, b) => a.localeCompare(b));
            setDcList(dcs);
        }
    }, [isAdmin, userPermissions]);

    // Sync selectedDC with the shared session key; default to first available DC if nothing stored
    useEffect(() => {
        if (dcList.length === 0) return;
        const stored = sessionStorage.getItem('selectedDC') || '';
        const initial = stored && dcList.includes(stored) ? stored : dcList[0];
        setSelectedDC(initial);
        sessionStorage.setItem('selectedDC', initial);
    }, [dcList]);

    // Phase 2: whenever a DC is selected, fetch that DC's groups WITH their members
    useEffect(() => {
        if (!selectedDC) return;
        setLoadingGroups(true);
        listGroupsByDC(selectedDC)
            .then((list) => {
                const normalised = list.map((g) => ({
                    ...g,
                    members: (g.members ?? []).map((m: any) =>
                        typeof m === 'string' ? m : m.userId
                    ),
                }));
                setGroups(normalised);
            })
            .catch((err: any) => {
                const data = err?.response?.data;
                const message =
                    data?.message ||
                    data?.error ||
                    (typeof data === 'string' ? data : null) ||
                    err?.message ||
                    'Failed to load groups.';
                toast.showError(message);
            })
            .finally(() => setLoadingGroups(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDC]);

    // Load the user pool used for group membership (names, avatars, the add-member picker)
    useEffect(() => {
        listUsers()
            .then(setUsers)
            .catch((err) => {
                console.error('Error loading users for groups:', err);
                setUsers([]);
            });
    }, []);

    const handleDCChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const dc = event.target.value;
        setSelectedDC(dc);
        sessionStorage.setItem('selectedDC', dc);
        broadcastDCChange(dc);
    };

    // Groups are already scoped to selectedDC from the API; apply text search on top
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return groups;
        return groups.filter(
            (g) =>
                (g.groupNm || g.groupId).toLowerCase().includes(q) ||
                g.description.toLowerCase().includes(q)
        );
    }, [groups, query]);

    const handleOpen = () => {
        setEditingGroupId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setOpen(true);
    };

    const [originalForm, setOriginalForm] = useState<GroupForm>(EMPTY_FORM);

    const handleEdit = (group: Group) => {
        const initial = { groupId: group.groupNm || group.groupId, description: group.description };
        setEditingGroupId(group.groupId);
        setForm(initial);
        setOriginalForm(initial);
        setErrors({});
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleChange = (field: keyof GroupForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleBlur = (field: keyof GroupForm) => () => {
        const value = (form[field] as string) ?? '';
        setErrors((prev) => {
            const next = { ...prev };
            if (field === 'groupId' && !editingGroupId) {
                if (!value.trim()) next.groupId = 'Group Name is required';
                else if (!VALIDATION.groupName.regex.test(value.trim())) next.groupId = VALIDATION.groupName.message;
                else if (groups.some((g) => g.groupId.toLowerCase() === value.trim().toLowerCase() && g.groupId !== editingGroupId)) next.groupId = 'A group with this name already exists';
                else delete next.groupId;
            }
            if (field === 'description') {
                if (!value.trim()) next.description = 'Description is required';
                else delete next.description;
            }
            return next;
        });
    };

    const validate = (): boolean => {
        const next: Partial<Record<keyof GroupForm, string>> = {};
        if (!(form.groupId ?? '').trim()) next.groupId = 'Group Name is required';
        else if (!VALIDATION.groupName.regex.test(form.groupId.trim())) next.groupId = VALIDATION.groupName.message;
        if (!(form.description ?? '').trim()) next.description = 'Description is required';

        const duplicate = groups.some(
            (g) =>
                g.groupId.toLowerCase() === form.groupId.trim().toLowerCase() &&
                g.groupId !== editingGroupId
        );
        if (duplicate) next.groupId = 'A group with this name already exists';

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const isFormValid = useMemo(() => {
        const nameOk = editingGroupId
            ? true
            : (form.groupId ?? '').trim() !== '' && VALIDATION.groupName.regex.test(form.groupId.trim());
        const descOk = (form.description ?? '').trim() !== '';
        return nameOk && descOk;
    }, [form, editingGroupId]);

    const isDirty = useMemo(() => {
        if (!editingGroupId) return true; // Add mode — always allow submit when valid
        return form.description.trim() !== originalForm.description.trim();
    }, [form, editingGroupId, originalForm]);

    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!validate()) return;
        const groupName = form.groupId.trim();
        const existingGroup = editingGroupId ? groups.find((g) => g.groupId === editingGroupId) : null;
        const payload = {
            groupId: groupName,
            groupNm: existingGroup ? (existingGroup.groupNm || groupName) : groupName,
            dstCntrCd: selectedDC,
            description: form.description.trim(),
            createUserId: actorId,
        };

        setSubmitting(true);
        try {
            if (editingGroupId) {
                await updateGroup(editingGroupId, payload);
                setGroups((prev) =>
                    prev.map((g) =>
                        g.groupId === editingGroupId
                            ? { ...g, groupNm: payload.groupNm, dstCntrCd: payload.dstCntrCd, description: payload.description }
                            : g
                    )
                );
                toast.showSuccess(`Group "${groupName}" updated successfully!`);
            } else {
                const created = await createGroup(payload);
                setGroups((prev) => [{ ...created, members: created.members ?? [] }, ...prev]);
                toast.showSuccess(`Group "${groupName}" created successfully!`);
            }
            setOpen(false);
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                'Failed to save group. Please try again.';
            toast.showError(message);
        } finally {
            setSubmitting(false);
        }
    };

    // ---- Delete group ----
    const [confirmDeleteGroup, setConfirmDeleteGroup] = useState<Group | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleDeleteConfirm = async () => {
        if (!confirmDeleteGroup) return;
        setDeleting(true);
        try {
            await deleteGroup(confirmDeleteGroup.groupId);
            setGroups((prev) => prev.filter((g) => g.groupId !== confirmDeleteGroup.groupId));
            toast.showSuccess(`Group "${confirmDeleteGroup.groupNm || confirmDeleteGroup.groupId}" deleted successfully.`);
            setConfirmDeleteGroup(null);
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                'Failed to delete group. Please try again.';
            toast.showError(message);
        } finally {
            setDeleting(false);
        }
    };

    // ---- Member management (user_group relationship) ----
    const [membersOpen, setMembersOpen] = useState(false);
    const [membersGroupId, setMembersGroupId] = useState<string | null>(null);
    const [membersGroupNm, setMembersGroupNm] = useState<string | null>(null);
    const [memberDraft, setMemberDraft] = useState<string[]>([]);
    const [memberQuery, setMemberQuery] = useState('');
    const [memberSearch, setMemberSearch] = useState('');
    const loadingMembers = false;

    const [originalMemberDraft, setOriginalMemberDraft] = useState<string[]>([]);

    const openMembers = (group: Group) => {
        const initial = [...(group.members ?? [])];
        setMembersGroupId(group.groupId);
        setMembersGroupNm(group.groupNm || group.groupId);
        setMemberDraft(initial);
        setOriginalMemberDraft(initial);
        setMemberQuery('');
        setMemberSearch('');
        setMembersOpen(true);
    };

    const isMembersDirty = useMemo(() => {
        if (memberDraft.length !== originalMemberDraft.length) return true;
        const orig = new Set(originalMemberDraft);
        return memberDraft.some((id) => !orig.has(id));
    }, [memberDraft, originalMemberDraft]);

    const closeMembers = () => setMembersOpen(false);

    const addMember = (userId: string) =>
        setMemberDraft((prev) => (prev.includes(userId) ? prev : [...prev, userId]));

    const removeMember = (userId: string) =>
        setMemberDraft((prev) => prev.filter((id) => id !== userId));

    const [savingMembers, setSavingMembers] = useState(false);

    const saveMembers = async () => {
        if (!membersGroupId) return;
        const group = groups.find((g) => g.groupId === membersGroupId);
        if (!group) return;
        setSavingMembers(true);
        try {
                await updateGroup(membersGroupId, {
                groupId: group.groupId,
                groupNm: group.groupNm,
                dstCntrCd: group.dstCntrCd,
                description: group.description,
                createUserId: actorId,
                members: memberDraft,
            });
            setGroups((prev) =>
                prev.map((g) => (g.groupId === membersGroupId ? { ...g, members: [...memberDraft] } : g))
            );
            setMembersOpen(false);
            toast.showSuccess(`Members updated for "${membersGroupNm || membersGroupId}".`);
            setTimeout(() => window.location.reload(), 1000);
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                'Failed to update members. Please try again.';
            toast.showError(message);
        } finally {
            setSavingMembers(false);
        }
    };

    // Current members (in draft order) and the pool of users available to add
    const memberUsers = memberDraft
        .map((id) => users.find((u) => u.userId === id))
        .filter((u): u is User => Boolean(u));

    const filteredMemberUsers = memberUsers.filter((u) => {
        const q = memberSearch.trim().toLowerCase();
        if (!q) return true;
        return (
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q)
        );
    });

    // Map of userId → groupId for users already assigned to OTHER groups in the same DC
    const usersInOtherGroups = useMemo<Map<string, string>>(() => {
        const map = new Map<string, string>();
        groups.forEach((g) => {
            if (g.groupId === membersGroupId) return;
            (g.members ?? []).forEach((uid) => map.set(uid, g.groupNm || g.groupId));
        });
        return map;
    }, [groups, membersGroupId]);

    const availableUsers = users.filter((u) => {
        if (memberDraft.includes(u.userId)) return false;
        const q = memberQuery.trim().toLowerCase();
        if (!q) return true;
        return (
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q) ||
            u.userId.toLowerCase().includes(q)
        );
    });

    return (
        <div className="groups-page">
            <div className="settings-title-row">
                <p className="settings-title">Groups</p>
                <div className="settings-title-actions">
                    <select
                        value={selectedDC}
                        onChange={handleDCChange}
                        disabled={dcList.length === 0}
                        className={`customSelect ${dcList.length === 0 ? 'disabled' : 'enabled'}`}
                    >
                        {dcList.length === 0 && <option value="">No DC available</option>}
                        {dcList.map((dc) => (
                            <option key={dc} value={dc}>{dc}</option>
                        ))}
                    </select>
                    <span className="groups-count">{filtered.length} groups</span>
                </div>
            </div>
            <div className="groups-card">
                <div className="groups-toolbar">
                    <div className="groups-search">
                        <Search size={16} className="groups-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by group name or description…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {query && (
                            <button className="groups-search-clear" onClick={() => setQuery('')} title="Clear">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    {isAdmin && (
                        <button className="groups-add-btn" onClick={handleOpen}>
                            <Plus size={16} />
                            Add Group
                        </button>
                    )}
                </div>

                <div className="groups-table-scroll">
                    <table className="groups-table">
                        <thead>
                            <tr>
                                <th className="group-col">Group Name</th>
                                <th>DC</th>
                                <th>Description</th>
                                <th>Members</th>
                                {isAdmin && <th className="action-col">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loadingGroups ? (
                                <tr>
                                    <td colSpan={isAdmin ? 5 : 4} className="groups-empty">
                                        Loading groups…
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin ? 5 : 4} className="groups-empty">
                                        No groups found{query ? ` for "${query}"` : ''}.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((g) => (
                                    <tr key={g.groupId}>
                                        <td className="group-cell">
                                            <div className="group-cell-inner">
                                                <span className="group-icon">
                                                    <UsersIcon size={15} />
                                                </span>
                                                {g.groupNm || g.groupId}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="dc-chip">{g.dstCntrCd}</span>
                                        </td>
                                        <td className="desc-cell">{g.description}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="member-chip member-chip-btn"
                                                onClick={() => openMembers(g)}
                                                title={isAdmin ? "Manage members" : "View members"}
                                            >
                                                <UsersIcon size={13} />
                                                {(g.members ?? []).length}
                                            </button>
                                        </td>
                                        {isAdmin && (
                                            <td className="action-col">
                                                <div className="row-action-group">
                                                    <button
                                                        type="button"
                                                        className="row-btn row-btn-members"
                                                        onClick={() => openMembers(g)}
                                                        title="Manage members"
                                                    >
                                                        <UserPlus size={15} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="row-btn row-btn-edit"
                                                        onClick={() => handleEdit(g)}
                                                        title="Edit group"
                                                    >
                                                        <Pencil size={15} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="row-btn row-btn-delete"
                                                        onClick={() => setConfirmDeleteGroup(g)}
                                                        title="Delete group"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog
                open={open}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') return;
                    handleClose();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>{editingGroupId ? 'Edit Group' : 'Add Group'}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            label="Group Name"
                            required
                            fullWidth
                            value={form.groupId}
                            onChange={handleChange('groupId')}
                            onBlur={handleBlur('groupId')}
                            error={!!errors.groupId}
                            helperText={editingGroupId ? 'Group Name cannot be changed' : errors.groupId}
                            disabled={!!editingGroupId}
                        />
                        <TextField
                            label="Description"
                            required
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.description}
                            onChange={handleChange('description')}
                            onBlur={handleBlur('description')}
                            error={!!errors.description}
                            helperText={errors.description}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleClose} color="inherit" disabled={submitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting || !isFormValid || !isDirty}>
                        {editingGroupId ? (submitting ? 'Saving…' : 'Save Changes') : (submitting ? 'Adding…' : 'Add Group')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage members (user_group) */}
            <Dialog
                open={membersOpen}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') return;
                    closeMembers();
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {isAdmin ? 'Manage Members' : 'View Members'}
                    {membersGroupNm && <span className="members-dialog-group"> — {membersGroupNm}</span>}
                </DialogTitle>
                <DialogContent dividers sx={{ overflow: 'hidden' }}>
                    {loadingMembers ? (
                        <div className="members-empty">Loading members…</div>
                    ) : (
                    <div className="members-two-col">
                        {/* ── Add users column (left) ── */}
                        {isAdmin && (
                        <div className="members-col">
                            <div className="members-section-label members-add-label">Add users</div>
                            <div className="members-search">
                                <Search size={15} className="members-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email…"
                                    value={memberQuery}
                                    onChange={(e) => setMemberQuery(e.target.value)}
                                />
                            </div>
                            <div className="members-scroll-box">
                                <div className="members-list members-pool">
                                    {availableUsers.length === 0 ? (
                                        <div className="members-empty">
                                            {memberQuery ? `No users match "${memberQuery}".` : 'All users are already members.'}
                                        </div>
                                    ) : (
                                        availableUsers.map((u) => {
                                            const blockedByGroup = usersInOtherGroups.get(u.userId);
                                            return (
                                                <div
                                                    key={u.userId}
                                                    className="member-row"
                                                    style={blockedByGroup ? { opacity: 0.5 } : undefined}
                                                    title={blockedByGroup ? `Already in group "${blockedByGroup}" within this DC` : undefined}
                                                >
                                                    <span className={`avatar ${avatarClassFor(u.userId)}`}>
                                                        {getInitials(u.firstName, u.lastName)}
                                                    </span>
                                                    <div className="member-info">
                                                        <span className="member-name">{u.firstName} {u.lastName}</span>
                                                        <span className="member-email">
                                                            {blockedByGroup
                                                                ? <span style={{ color: '#e07b00', fontStyle: 'italic' }}>Already in: {blockedByGroup}</span>
                                                                : u.email}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="member-action member-add"
                                                        onClick={() => !blockedByGroup && addMember(u.userId)}
                                                        disabled={!!blockedByGroup}
                                                        title={blockedByGroup ? `Already assigned to "${blockedByGroup}"` : 'Add to group'}
                                                        style={blockedByGroup ? { cursor: 'not-allowed' } : undefined}
                                                    >
                                                        <UserPlus size={15} />
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                        )}

                        {/* ── Members column (right) ── */}
                        <div className="members-col">
                            <div className="members-section-label">
                                Members <span className="members-section-count">{memberUsers.length}</span>
                            </div>
                            <div className="members-search">
                                <Search size={15} className="members-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email…"
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                />
                            </div>
                            <div className="members-scroll-box">
                                {filteredMemberUsers.length === 0 ? (
                                    <div className="members-empty">
                                        {memberSearch ? `No members match "${memberSearch}".` : (isAdmin ? 'No members yet.' : 'No members in this group.')}
                                    </div>
                                ) : (
                                    <div className="members-list">
                                        {filteredMemberUsers.map((u) => (
                                            <div key={u.userId} className="member-row">
                                                <span className={`avatar ${avatarClassFor(u.userId)}`}>
                                                    {getInitials(u.firstName, u.lastName)}
                                                </span>
                                                <div className="member-info">
                                                    <span className="member-name">{u.firstName} {u.lastName}</span>
                                                    <span className="member-email">{u.email}</span>
                                                </div>
                                                {isAdmin && (
                                                    <button
                                                        type="button"
                                                        className="member-action member-remove"
                                                        onClick={() => removeMember(u.userId)}
                                                        title="Remove from group"
                                                    >
                                                        <UserMinus size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={closeMembers} color="inherit" disabled={savingMembers || loadingMembers}>
                        {isAdmin ? 'Cancel' : 'Close'}
                    </Button>
                    {isAdmin && (
                        <Button onClick={saveMembers} variant="contained" disabled={savingMembers || loadingMembers || !isMembersDirty}>
                            {savingMembers ? 'Saving…' : 'Save Members'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={!!confirmDeleteGroup}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') return;
                    setConfirmDeleteGroup(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Delete Group</DialogTitle>
                <DialogContent>
                    <p style={{ margin: '8px 0 0', fontSize: 14, color: '#344054' }}>
                        Are you sure you want to delete group{' '}
                        <strong>"{confirmDeleteGroup?.groupNm || confirmDeleteGroup?.groupId}"</strong>?
                        This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setConfirmDeleteGroup(null)} color="inherit" disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Groups;
